/**
 * Admin module request config — the EXPERTS-audience scope for the admin
 * endpoint that BYPASSES the @andavo/web-ui/api configureApi wrapper
 * (kb-upload.ts multipart PDF ingest). The experts backend serves BOTH
 * /api/chat/* and /api/admin/*, so admin calls carry the SAME experts-audience
 * token as chat — passed explicitly to the scope-parameterized
 * acquireToken(scopes) (the auth_unification seam), NOT the parameterless call,
 * NOT a hardcoded literal.
 *
 * Resolved from build-time env so the scope tracks whatever the Entra topology
 * turns out to be:
 *   - VITE_EXPERTS_SCOPES — explicit experts-audience scope(s); preferred.
 *   - VITE_CHAT_SCOPES    — same experts backend; honored as a fallback so a
 *                           single env var configures both modules.
 *   - else the full configured scope union (single-registration dev default —
 *     every scope mints from the one SPA app, so the union IS the experts token).
 *
 * NOTE: the rest of the admin feature (admin-me-query, admin-queries) goes
 * through the `api` wrapper, whose default resolver requests the full union —
 * valid for the experts backend in the single-registration topology. A future
 * per-resource topology would thread per-call scopes through the wrapper; that
 * is a configureApi change tracked separately, not an admin concern today.
 */
import { loadAuthConfig } from '@/auth/config';

function parseScopes(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** The EXPERTS-audience scopes acquireToken() should mint a token for. */
export function getExpertsScopes(): string[] {
  const explicit =
    parseScopes(import.meta.env.VITE_EXPERTS_SCOPES) ||
    parseScopes(import.meta.env.VITE_CHAT_SCOPES);
  if (explicit.length > 0) return explicit;
  return loadAuthConfig().scopes;
}

/** Base URL for the admin endpoints (same-origin by default). */
export function getAdminBaseUrl(): string {
  return import.meta.env.VITE_ADMIN_API_BASE_URL ?? '';
}

/** Join a same-origin or absolute base with a relative API path. */
export function adminUrl(path: string): string {
  const base = getAdminBaseUrl();
  if (!base) return path;
  return `${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
}
